<template>
  <div
    v-if="bundles.length > 0"
    class="quick-adds"
  >
    <div class="quick-adds__title">{{ cartSidebarContent.quickAddsHeading }}</div>
    <div
      ref="emblaRef"
      class="quick-adds__viewport"
      role="region"
      :aria-roledescription="a11yContent.carouselRoleDescription"
      :aria-label="cartSidebarContent.quickAddsAriaLabel"
    >
      <div class="quick-adds__container">
        <QuickAddItem
          v-for="bundle in bundles"
          :key="bundle.catalogBundleId"
          :bundle="bundle"
          :disabled="disabled"
          class="quick-adds__slide"
          @add="$emit('add', bundle)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import useEmblaCarousel from '@/composables/emblaCarousel'

import { CatalogBundleStorefrontDtoWithContent } from '@/types/CatalogBundleStorefrontDtoWithContent'

import QuickAddItem from './QuickAddItem.vue'
import { a11yContent } from '@/content/a11y'
import { cartSidebarContent } from '@/content/cartSidebar'

export default defineComponent({
  name: 'QuickAdds',
  components: { QuickAddItem },
  props: {
    bundles: {
      type: Array as PropType<CatalogBundleStorefrontDtoWithContent[]>,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['add'],
  setup() {
    // Finite, swipe/drag carousel (no infinite loop) matching the Figma YMAL row. `trimSnaps` drops
    // the empty trailing snap so the last card lands flush at the edge rather than over-scrolling.
    return {
      ...useEmblaCarousel({
        options: { loop: false, align: 'start', containScroll: 'trimSnaps' }
      }),
      cartSidebarContent,
      a11yContent
    }
  },
  watch: {
    // The flyout keeps this mounted while hidden (the cross-sell pool is empty until the cart has a
    // subtotal) and the pool changes as items are added/removed. Embla only measures slides on init,
    // so re-init whenever the slide set changes (count change → recalculated snaps; 0 → N → the
    // viewport that was absent at mount now exists). `initEmbla` destroys any prior instance first.
    bundles() {
      this.$nextTick(() => this.initEmbla())
    }
  }
})
</script>

<style scoped lang="scss">
.quick-adds {
  &__title {
    font-weight: 700;
    margin-bottom: 0.75rem;
  }

  // Embla viewport (Figma `10192:196341`): a finite swipeable row instead of a native scrollbar.
  &__viewport {
    overflow: hidden;
    // Drag-to-swipe shouldn't text-select the card content (titles/prices) mid-gesture.
    user-select: none;
  }

  &__container {
    display: flex;
    gap: 1rem;
    // Breathing room so the cards' drop shadow isn't clipped flat by the viewport's overflow.
    padding-bottom: 0.5rem;
  }

  // Cards keep their intrinsic width (set on `.quick-add-item`); they must not shrink to fit the
  // viewport, so Embla can scroll between them.
  &__slide {
    flex: 0 0 auto;
  }
}
</style>
